import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Header } from '../components/Header';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { AdminSettings } from '../components/AdminSettings';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Users, 
  Clock, 
  HandHeart, 
  MessageSquare,
  ArrowRightLeft,
  TrendingUp,
  Search,
  Trash2,
  Edit,
  Loader2,
  BarChart3,
  Settings as SettingsIcon
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function AdminDashboardPage() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [exchanges, setExchanges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [editingUser, setEditingUser] = useState(null);
  const [newCredits, setNewCredits] = useState(0);
  const [submitting, setSubmitting] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [statsRes, usersRes, exchangesRes] = await Promise.all([
        axios.get(`${API_URL}/api/admin/stats`, { withCredentials: true }),
        axios.get(`${API_URL}/api/admin/users`, { withCredentials: true }),
        axios.get(`${API_URL}/api/admin/exchanges`, { withCredentials: true })
      ]);
      setStats(statsRes.data);
      setUsers(usersRes.data);
      setExchanges(exchangesRes.data);
    } catch (err) {
      if (process.env.NODE_ENV !== 'production') console.error('Error fetching admin data:', err);
      toast.error('Failed to load admin data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleUpdateCredits = async () => {
    if (!editingUser) return;
    setSubmitting(true);
    try {
      await axios.put(`${API_URL}/api/admin/users/${editingUser.user_id}/credits`, 
        { credits: newCredits }, 
        { withCredentials: true }
      );
      setUsers(users.map(u => u.user_id === editingUser.user_id ? {...u, time_credits: newCredits} : u));
      setEditingUser(null);
      toast.success('Credits updated successfully');
    } catch (err) {
      toast.error('Failed to update credits');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!confirm('Are you sure you want to delete this user? This cannot be undone.')) return;
    try {
      await axios.delete(`${API_URL}/api/admin/users/${userId}`, { withCredentials: true });
      setUsers(users.filter(u => u.user_id !== userId));
      toast.success('User deleted');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete user');
    }
  };

  const filteredUsers = users.filter(u => 
    u.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    u.email?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-[#ffd703]/10 text-[#ffd703]',
      confirmed: 'bg-[#d1c7e9]/10 text-[#d1c7e9]',
      completed: 'bg-[#22c55e]/10 text-[#22c55e]',
      cancelled: 'bg-[#ef4444]/10 text-[#ef4444]'
    };
    return <Badge className={styles[status] || styles.pending}>{status}</Badge>;
  };

  if (loading) {
    return (
      <div className="min-h-screen">
        <Header />
        <div className="flex justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-[#ffd703]" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      <Header />
      
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="admin-dashboard">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-2xl sm:text-3xl font-semibold text-[#090000] mb-2">
            Admin Dashboard
          </h1>
          <p className="text-[#090000/60]">
            Manage users, exchanges, and monitor platform activity
          </p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 lg:grid-cols-6 gap-4 mb-8">
          <Card className="" data-testid="stat-total-users">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-[#ffd703]/10 flex items-center justify-center">
                  <Users className="w-5 h-5 text-[#ffd703]" />
                </div>
                <div>
                  <p className="text-2xl font-semibold text-[#090000]">{stats?.total_users || 0}</p>
                  <p className="text-xs text-[#090000/60]">
                    Total Users
                    {typeof stats?.total_businesses === 'number' && (
                      <span className="ml-1 text-black/50">
                        · {stats.total_businesses} biz
                      </span>
                    )}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="" data-testid="stat-total-offers">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-[#22c55e]/10 flex items-center justify-center">
                  <HandHeart className="w-5 h-5 text-[#22c55e]" />
                </div>
                <div>
                  <p className="text-2xl font-semibold text-[#090000]">{stats?.total_offers || 0}</p>
                  <p className="text-xs text-[#090000/60]">Skill Offers</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="" data-testid="stat-total-requests">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-[#d1c7e9]/10 flex items-center justify-center">
                  <MessageSquare className="w-5 h-5 text-[#d1c7e9]" />
                </div>
                <div>
                  <p className="text-2xl font-semibold text-[#090000]">{stats?.total_requests || 0}</p>
                  <p className="text-xs text-[#090000/60]">Help Requests</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="" data-testid="stat-total-exchanges">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-[#ffd703]/10 flex items-center justify-center">
                  <ArrowRightLeft className="w-5 h-5 text-[#ffd703]" />
                </div>
                <div>
                  <p className="text-2xl font-semibold text-[#090000]">{stats?.total_exchanges || 0}</p>
                  <p className="text-xs text-[#090000/60]">Total Exchanges</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="" data-testid="stat-completed-exchanges">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-[#22c55e]/10 flex items-center justify-center">
                  <TrendingUp className="w-5 h-5 text-[#22c55e]" />
                </div>
                <div>
                  <p className="text-2xl font-semibold text-[#090000]">{stats?.completed_exchanges || 0}</p>
                  <p className="text-xs text-[#090000/60]">Completed</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="" data-testid="stat-total-hours">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-[#ffd703]/10 flex items-center justify-center">
                  <Clock className="w-5 h-5 text-[#ffd703]" />
                </div>
                <div>
                  <p className="text-2xl font-semibold text-[#090000]">{stats?.total_hours_exchanged?.toFixed(1) || 0}</p>
                  <p className="text-xs text-[#090000/60]">Hours Exchanged</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="users">
          <TabsList className="bg-[#c7dfe9] mb-6">
            <TabsTrigger value="users" className="data-[state=active]:bg-white" data-testid="tab-users">
              <Users className="w-4 h-4 mr-2" />
              Users ({users.length})
            </TabsTrigger>
            <TabsTrigger value="exchanges" className="data-[state=active]:bg-white" data-testid="tab-exchanges">
              <ArrowRightLeft className="w-4 h-4 mr-2" />
              Exchanges ({exchanges.length})
            </TabsTrigger>
            <TabsTrigger value="settings" className="data-[state=active]:bg-white" data-testid="tab-settings">
              <SettingsIcon className="w-4 h-4 mr-2" />
              Settings
            </TabsTrigger>
          </TabsList>

          <TabsContent value="users">
            <Card className="">
              <CardHeader>
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                  <CardTitle className="text-lg font-medium text-[#090000]">User Management</CardTitle>
                  <div className="relative w-full sm:w-64">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#090000/60]" />
                    <Input
                      placeholder="Search users..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-10"
                      data-testid="search-users"
                    />
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>User</TableHead>
                        <TableHead>Role</TableHead>
                        <TableHead>Credits</TableHead>
                        <TableHead>Skills</TableHead>
                        <TableHead>Joined</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredUsers.map((u) => (
                        <TableRow key={u.user_id} data-testid={`user-row-${u.user_id}`}>
                          <TableCell>
                            <div>
                              <p className="font-medium text-[#090000] flex items-center gap-1.5">
                                {u.account_type === 'business' && u.business_name ? u.business_name : u.name}
                                {u.account_type === 'business' && (
                                  <Badge className="bg-[#d8b4fe] text-black text-[10px] px-1.5 py-0" data-testid={`admin-biz-badge-${u.user_id}`}>
                                    biz
                                  </Badge>
                                )}
                              </p>
                              <p className="text-xs text-[#090000/60]">{u.email}</p>
                              {u.account_type === 'business' && u.name && u.business_name && (
                                <p className="text-[10px] text-black/50">Contact: {u.name}</p>
                              )}
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge className={u.role === 'admin' ? 'bg-[#ffd703]/10 text-[#ffd703]' : 'bg-[#d1c7e9]/10 text-[#d1c7e9]'}>
                              {u.role}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <span className="flex items-center gap-1">
                              <Clock className="w-4 h-4 text-[#ffd703]" />
                              {u.time_credits?.toFixed(1) || 0}
                            </span>
                          </TableCell>
                          <TableCell>
                            <div className="flex flex-wrap gap-1 max-w-xs">
                              {u.skills?.slice(0, 2).map((skill, i) => (
                                <Badge key={i} variant="outline" className="text-xs">{skill}</Badge>
                              ))}
                              {u.skills?.length > 2 && (
                                <Badge variant="outline" className="text-xs">+{u.skills.length - 2}</Badge>
                              )}
                            </div>
                          </TableCell>
                          <TableCell className="text-sm text-[#090000/60]">
                            {u.created_at ? new Date(u.created_at).toLocaleDateString() : 'N/A'}
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex justify-end gap-2">
                              <Dialog>
                                <DialogTrigger asChild>
                                  <Button 
                                    variant="outline" 
                                    size="sm"
                                    onClick={() => {
                                      setEditingUser(u);
                                      setNewCredits(u.time_credits || 0);
                                    }}
                                    className=""
                                    data-testid={`edit-credits-${u.user_id}`}
                                  >
                                    <Edit className="w-4 h-4" />
                                  </Button>
                                </DialogTrigger>
                                <DialogContent>
                                  <DialogHeader>
                                    <DialogTitle>Update Credits for {editingUser?.name}</DialogTitle>
                                    <DialogDescription>Set the user's time credit balance manually.</DialogDescription>
                                  </DialogHeader>
                                  <div className="space-y-4 mt-4">
                                    <div className="space-y-2">
                                      <label className="text-sm font-medium">Time Credits</label>
                                      <Input
                                        type="number"
                                        step="0.5"
                                        value={newCredits}
                                        onChange={(e) => setNewCredits(parseFloat(e.target.value))}
                                        data-testid="new-credits-input"
                                      />
                                    </div>
                                    <div className="flex justify-end gap-3">
                                      <Button variant="outline" onClick={() => setEditingUser(null)}>
                                        Cancel
                                      </Button>
                                      <Button 
                                        onClick={handleUpdateCredits}
                                        className="bg-[#ffd703] hover:bg-[#e6c203]"
                                        disabled={submitting}
                                        data-testid="save-credits-btn"
                                      >
                                        {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                                        Save
                                      </Button>
                                    </div>
                                  </div>
                                </DialogContent>
                              </Dialog>
                              {u.role !== 'admin' && (
                                <Button 
                                  variant="outline" 
                                  size="sm"
                                  onClick={() => handleDeleteUser(u.user_id)}
                                  className="border-[#ef4444] text-[#ef4444] hover:bg-[#ef4444]/10"
                                  data-testid={`delete-user-${u.user_id}`}
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="exchanges">
            <Card className="">
              <CardHeader>
                <CardTitle className="text-lg font-medium text-[#090000]">Exchange History</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Date</TableHead>
                        <TableHead>Participants</TableHead>
                        <TableHead>Description</TableHead>
                        <TableHead>Hours</TableHead>
                        <TableHead>Status</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {exchanges.slice(0, 20).map((exchange) => (
                        <TableRow key={exchange.exchange_id} data-testid={`exchange-row-${exchange.exchange_id}`}>
                          <TableCell className="text-sm text-[#090000/60]">
                            {new Date(exchange.created_at).toLocaleDateString()}
                          </TableCell>
                          <TableCell>
                            <span className="text-sm">
                              {exchange.initiator_name} → {exchange.partner_name}
                            </span>
                          </TableCell>
                          <TableCell className="max-w-xs truncate">
                            {exchange.description}
                          </TableCell>
                          <TableCell>
                            <span className="flex items-center gap-1">
                              <Clock className="w-4 h-4 text-[#ffd703]" />
                              {exchange.hours}
                            </span>
                          </TableCell>
                          <TableCell>
                            {getStatusBadge(exchange.status)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="settings" data-testid="settings-tab-content">
            <AdminSettings />
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
